import React from 'react';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/context-graphs-point-in-time.jpg
import pointInTimeImg from '../../../assets/images/blogs/context-graphs-point-in-time.jpg';

const ContextGraphsPointInTime = () => (
    <BlogPost
        title="Context Graphs: State, Knowledge, Decision - Truth in the Moment"
        subtitle="Memory isn't abstract. Memory is point in time."
        category="Architecture"
        categoryIcon={faHistory}
        color="#003366"
        date="February 2026"
        readTime="4 min read"
        image={pointInTimeImg}
        imageAlt="Context graphs - capturing truth in the moment, not universal abstractions"
        ctaHeading="Ready for point-in-time knowledge?"
        ctaText="Book a discovery call and we'll show you how the Knowledge Flywheel captures evolution naturally through execution."
        seoDescription="Why context graphs capture truth in the moment rather than maintaining universal definitions, and how this solves the knowledge drift problem."
        seoKeywords="context graphs, temporal knowledge, point in time, knowledge drift, organizational memory, Nebulyx"
        slug="context-graphs-point-in-time"
    >
        <p>
            After more excellent conversations about context graphs, I've been reflecting
            on why there's so much "heated" debate. 🤔
        </p>

        <p>
            One key aspect keeps surfacing: the difference between point-in-time knowledge
            vs abstracting concepts, definitions, and flows ahead of time.
        </p>

        <h3>Traditional Knowledge Management</h3>

        <p>
            Abstract meaning out of context into universal definitions.
        </p>

        <p>
            Define flows, ontologies, schemas upfront. Maintain one "true" meaning of terms.
        </p>

        <p>
            <strong>Problem:</strong> meaning drifts, flows break, maintenance never ends.
        </p>

        <p>
            <strong>Solution:</strong> governance cycles, tracking drift, updating
            definitions. ⏳
        </p>

        <h3>Context Graphs</h3>

        <p>
            State, knowledge, and decision captured as one - in the moment.
        </p>

        <p>
            Each decision trace is temporally bound. No universal definition to drift.
        </p>

        <p>
            Flows emerge through execution, not upfront design. 🎯
        </p>

        <h3>Example: Customer Discount Approval</h3>

        <p>
            <strong>March 2024:</strong> Customer gets 15% discount.
        </p>

        <p>
            At that moment: spend history £50k, policy allowed up to 15% for that tier,
            sales VP approved, Q1 targets were behind. ✅
        </p>

        <p>
            <strong>January 2026:</strong> Same customer, same request.
        </p>

        <p>
            But now: spend dropped to £30k, tier changed, policy updated to max 10%,
            different approver required. ❌
        </p>

        <p>
            Same customer. Same ask. <strong>Different truth in each moment.</strong>
        </p>

        <blockquote className="blog-post-quote">
            "Traditional KM asks 'what's our discount policy?' (universal, abstract).
            Context graphs capture 'what was true when this decision was made?'
            (contextual, temporal)."
        </blockquote>

        <h3>Organisational Memory</h3>

        <p>
            This is why context graphs work as organisational memory. 🧠
        </p>

        <p>
            Memory isn't abstract. Memory is point in time.
        </p>

        <p>
            What happened, when, why, with what information.
        </p>

        <h3>The Paradigm Shift</h3>

        <p>
            Traditional knowledge management tries to maintain universal truth.
        </p>

        <p>
            <strong>Context graphs capture truth in the moment.</strong> 💡
        </p>

        <p>
            That's the paradigm shift.
        </p>

        <p>
            Not just a new technology. A different way of thinking about knowledge itself.
        </p>

        <p>
            The Knowledge Flywheel doesn't fight drift. It captures evolution naturally
            through execution. 🎯
        </p>
    </BlogPost>
);

export default ContextGraphsPointInTime;
